/**
 * 다중 스트림 콘텐츠 라우팅 프리미티브 (ADR-0060).
 *
 * 같은 글 풀(post)을 둘 이상의 스트림으로 분리(공지=게시판 /notice, 블로그=그리드
 * /blog 등). `cms-renderer-next` / `cms-renderer-astro` 가 동일 로직을 공유하도록
 * 여기(cms-core)에 둔다 — cms-client·프레임워크 비의존(구조적 타입만).
 *
 * 설계(codex consult `019ef577` 반영):
 * - **활성화 = `collections.length > 0`.** 빈 배열·미전달 = 레거시 단일 /blog.
 * - `RouteCollection` 은 **라우팅 최소셋**만(key/basePath/categories/feed/archives와
 *   경로 정책) 가진다.
 *   label/layout/icon 등 표시·작성 메타는 templates `CollectionSchema` 에 둔다.
 */

/** 콘텐츠 스트림 1개 — sitemap/feed/아카이브 파생의 라우팅 단위. */
export interface RouteCollection {
  /** 안정 식별자 (예: "notice" | "blog"). */
  readonly key: string;
  /**
   * 라우트 base path (예: "/notice" | "/blog"). 앞 슬래시 포함, 뒤 슬래시 없이.
   * **`""`(빈 문자열) = 주소 없는 유형** — 페이지 안에서 불러와 쓰는 콘텐츠(강사·리뷰
   * 등)로, 라우트/사이트맵/피드/아카이브를 일절 파생하지 않는다. 외부 사이트는
   * posts API 의 `collectionKey` 필터로 내용만 가져다 렌더한다. `resolvePostUrl`/
   * `resolvePostPath` 는 이 값이 빈 문자열이면 항상 null. 소속 판정
   * (`resolvePostCollection`)에는 영향 없음 — key 만으로 소속이 성립한다.
   */
  readonly basePath: string;
  /**
   * 이 섹션이 제공하는 **주제(category) allowlist** — `${basePath}/categories/{slug}`
   * 아카이브 라우트 범위. ADR-0060 Amendment 1 (M4): 섹션 소속은 글의 `collectionKey`
   * 로 정해지므로 이 배열은 더 이상 라우팅 소유권이 아니다(catch-all 개념 폐기). 빈
   * 배열 = 이 섹션은 주제 없음.
   */
  readonly categories: readonly string[];
  /** RSS 피드 포함 여부 (기본 false). */
  readonly feed?: boolean;
  /** `${basePath}/categories/{slug}` 아카이브 emit 여부 (기본 false). */
  readonly archives?: boolean;
  /**
   * 글별 상세 라우트 존재 여부 (기본 true — 하위 호환). false 면 이 스트림은
   * "목록 전용" 유형(예: 후기 — 목록에서 전문 노출, 글마다 별도 상세 URL 없음).
   * `resolvePostUrl`/`resolvePostPath` 가 null 을 반환해 sitemap/feed 에서 글
   * URL 을 발행하지 않는다. 소속 판정(`resolvePostCollection`)에는 영향 없음
   * — 소속과 상세 URL 존재 여부는 별개(M4 원칙 연장).
   */
  readonly detail?: boolean;
  /**
   * 글 상세 주소 형식. `flat`(기본) = `${basePath}/{postSlug}`,
   * `category` = `${basePath}/{categorySlug}/{postSlug}`.
   * category 모드는 URL을 하나로 결정해야 하므로 `categoryCardinality: "exactly-one"`
   * 과 함께 쓴다.
   */
  readonly detailPath?: "flat" | "category";
  /**
   * 카테고리 허브 주소 형식. `namespaced`(기본) =
   * `${basePath}/categories/{slug}`, `direct` = `${basePath}/{slug}`.
   */
  readonly categoryPath?: "namespaced" | "direct";
  /** 글에 허용하는 category term 개수. 기본 `multiple`(기존 동작). */
  readonly categoryCardinality?: "multiple" | "exactly-one";
}

/** resolve 입력 — 글의 구조적 최소 형태(cms-client `CmsPostContent` 와 구조 호환). */
export interface RoutablePost {
  readonly slug: string;
  /** 글의 주제(category) terms — 아카이브 라우트 필터용. 섹션 판정엔 쓰지 않는다(M4). */
  readonly terms: ReadonlyArray<{ readonly taxonomy: string; readonly slug: string }>;
  /**
   * ADR-0060 Amendment 1 (Option A, M4) — 글에 명시된 섹션 key. 섹션 판정의 유일한
   * 근거. 미설정/미존재면 어느 스트림에도 안 속함(null). 카테고리 파생 fallback 은 제거됨.
   */
  readonly collectionKey?: string | null;
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/** collection 모드인지 — 길이 0(또는 미전달)이면 레거시 단일 /blog 동작. */
export function isInCollectionMode(
  collections: readonly RouteCollection[] | undefined,
): boolean {
  return !!collections && collections.length > 0;
}

/**
 * 글이 속한 collection 1개를 해석 — ADR-0060 Amendment 1 (Option A, M4): **글의
 * 명시 `collectionKey` 만** 본다. 미설정/미존재 key 면 null(상세 라우트 없는 글 →
 * sitemap 제외 신호). 카테고리 slug 파생·catch-all·first-wins 는 M4 에서 제거됨 —
 * 섹션은 카테고리가 아니라 글에 명시한다. (`categories` 는 이제 주제 allowlist 로만,
 * 아카이브 라우트에서 쓰임.)
 */
export function resolvePostCollection(
  post: RoutablePost,
  collections: readonly RouteCollection[],
): RouteCollection | null {
  if (!post.collectionKey) return null;
  return collections.find((c) => c.key === post.collectionKey) ?? null;
}

/**
 * 글의 정규 절대 URL — 소속 스트림 basePath 기준. 어느 스트림에도 안 속하거나,
 * 소속 스트림이 `detail: false`(목록 전용 유형) 이거나, `basePath` 가 빈 문자열
 * (주소 없는 유형)이면 null. slug 는 percent-encoding (한글 slug 안전).
 */
export function resolvePostUrl(
  siteUrl: string,
  post: RoutablePost,
  collections: readonly RouteCollection[],
): string | null {
  const path = resolvePostPath(post, collections);
  return path ? `${trimTrailingSlash(siteUrl)}${path}` : null;
}

/**
 * category 기반 상세 주소에 사용할 단일 category slug. collection 에 연결된
 * category만 세고 정확히 1개일 때만 반환한다. 0개·2개 이상이면 정규 URL을
 * 임의 선택하지 않고 null 로 닫는다.
 */
export function resolvePostCategorySlug(
  post: RoutablePost,
  collection: RouteCollection,
): string | null {
  const allowed = new Set(collection.categories);
  const slugs = [
    ...new Set(
      post.terms
        .filter(
          (term) => term.taxonomy === "category" && allowed.has(term.slug),
        )
        .map((term) => term.slug),
    ),
  ];
  return slugs.length === 1 ? slugs[0]! : null;
}

/** 카테고리 허브 경로. archives 가 꺼졌거나 주소가 없으면 null. */
export function resolveCategoryPath(
  collection: RouteCollection,
  categorySlug: string,
): string | null {
  if (!collection.archives || !collection.basePath || !categorySlug) return null;
  const basePath = trimTrailingSlash(collection.basePath);
  const encodedSlug = encodeURIComponent(categorySlug);
  return collection.categoryPath === "direct"
    ? `${basePath}/${encodedSlug}`
    : `${basePath}/categories/${encodedSlug}`;
}

/**
 * 글의 *경로*(siteUrl 없는 basePath-상대) — 예: "/blog/hello". 소속 스트림이
 * 없거나, `detail: false`(목록 전용 유형) 이거나, `basePath` 가 빈 문자열
 * (주소 없는 유형)이면 null. astro 처럼 상대 href 가 필요한 소비자용
 * (`resolvePostUrl` 은 절대 URL).
 */
export function resolvePostPath(
  post: RoutablePost,
  collections: readonly RouteCollection[],
): string | null {
  const collection = resolvePostCollection(post, collections);
  if (!collection) return null;
  if (collection.detail === false) return null;
  if (!collection.basePath) return null;
  const basePath = trimTrailingSlash(collection.basePath);
  const encodedPostSlug = encodeURIComponent(post.slug);
  if (collection.detailPath !== "category") {
    return `${basePath}/${encodedPostSlug}`;
  }
  const categorySlug = resolvePostCategorySlug(post, collection);
  return categorySlug
    ? `${basePath}/${encodeURIComponent(categorySlug)}/${encodedPostSlug}`
    : null;
}

/**
 * 특정 collection에 명시적으로 귀속된 글만 남긴다.
 */
export function postsForCollection<T extends RoutablePost>(
  posts: readonly T[],
  collections: readonly RouteCollection[],
  key: string,
): T[] {
  return posts.filter((p) => resolvePostCollection(p, collections)?.key === key);
}

/** feed/sitemap 등 파생에서 제외할, 어느 스트림에도 안 속한 글 제거. */
export function postsInAnyCollection<T extends RoutablePost>(
  posts: readonly T[],
  collections: readonly RouteCollection[],
): T[] {
  return posts.filter((p) => resolvePostCollection(p, collections) !== null);
}

/**
 * `postsInAnyCollection` 의 대칭 — **어느 스트림에도 안 속한("미소속") 글만** 남긴다.
 * 판정 시맨틱 = `resolvePostCollection(post, collections) === null`(collectionKey 가
 * NULL 이거나 사이트 선언 컬렉션 밖). `/blog` 처럼 "선언 스트림에 안 잡힌 글 = 블로그"로
 * 취급하는 표면에서 스트림 글 누출을 막는 데 쓴다(`blog-list-collection-scope.md` §2①).
 */
export function postsNotInAnyCollection<T extends RoutablePost>(
  posts: readonly T[],
  collections: readonly RouteCollection[],
): T[] {
  return posts.filter((p) => resolvePostCollection(p, collections) === null);
}

/**
 * config 검증 — 중복 key/basePath, 그리고 **두 스트림에 동시 등장하는 category slug**
 * (라우팅 모호성)를 탐지. `basePath === ""`(주소 없는 유형)는 basePath 형식·중복
 * 검사를 건너뛴다 — 라우트가 없으므로 여러 개 공존해도 모호성이 없다. key 중복·
 * category 중복 검사는 주소 유무와 무관하게 전 항목에 적용된다. 문제 메시지 배열
 * 반환(빈 = 정상). 빌드 타임 fail-fast 용.
 */
export function validateCollections(collections: readonly RouteCollection[]): string[] {
  const problems: string[] = [];
  const seenKey = new Set<string>();
  const seenBase = new Set<string>();
  const categoryOwner = new Map<string, string>();

  for (const c of collections) {
    if (seenKey.has(c.key)) problems.push(`중복 collection key: "${c.key}"`);
    seenKey.add(c.key);

    if (c.basePath !== "") {
      const base = trimTrailingSlash(c.basePath);
      if (seenBase.has(base)) problems.push(`중복 basePath: "${c.basePath}"`);
      seenBase.add(base);

      if (!c.basePath.startsWith("/")) {
        problems.push(`basePath 는 "/" 로 시작해야 함: "${c.basePath}" (${c.key})`);
      } else if (
        base.length < 2 ||
        base.startsWith("//") ||
        /[\\?#\s]/.test(base) ||
        base.split("/").some((segment) => segment === "." || segment === "..")
      ) {
        problems.push(
          `basePath 는 사이트 내부 절대 경로여야 함: "${c.basePath}" (${c.key})`,
        );
      }
    }

    for (const slug of c.categories) {
      const owner = categoryOwner.get(slug);
      if (owner && owner !== c.key) {
        problems.push(
          `category "${slug}" 가 collection "${owner}" 와 "${c.key}" 에 중복 — 한 카테고리는 한 스트림에만.`,
        );
      }
      categoryOwner.set(slug, c.key);
    }

    if (c.detailPath === "category") {
      if (!c.basePath) {
        problems.push(
          `category 상세 경로는 basePath 가 필요함: "${c.key}"`,
        );
      }
      if (c.detail === false) {
        problems.push(
          `category 상세 경로는 글별 상세 페이지가 필요함: "${c.key}"`,
        );
      }
      if (!c.archives) {
        problems.push(
          `category 상세 경로는 카테고리 아카이브가 필요함: "${c.key}"`,
        );
      }
      if (c.categoryCardinality !== "exactly-one") {
        problems.push(
          `category 상세 경로는 categoryCardinality "exactly-one" 이 필요함: "${c.key}"`,
        );
      }
    }
  }
  return problems;
}
